import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { ArrowLeft, ChevronDown, ChevronUp, Download, Lock, QrCode, ShieldCheck, Smartphone, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';

const QrCodeGeneratorPage = () => {
  const [content, setContent] = useState('https://allsmalltools.com');
  const [size, setSize] = useState(320);
  const [foreground, setForeground] = useState('#111827');
  const [background, setBackground] = useState('#ffffff');
  const [errorCorrection, setErrorCorrection] = useState('M');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(
    () => localStorage.getItem('qr-generator-bookmark-dismissed') !== 'true'
  );

  useEffect(() => {
    let cancelled = false;

    const generateQrCode = async () => {
      if (!content.trim()) {
        setQrDataUrl('');
        setError('Enter text or a URL to generate a QR code.');
        return;
      }

      try {
        const result = await QRCode.toDataURL(content, {
          width: size,
          margin: 2,
          errorCorrectionLevel: errorCorrection,
          color: { dark: foreground, light: background },
        });
        if (!cancelled) {
          setQrDataUrl(result);
          setError('');
        }
      } catch (generationError) {
        if (!cancelled) {
          setQrDataUrl('');
          setError(generationError.message || 'Unable to generate this QR code.');
        }
      }
    };

    generateQrCode();
    return () => { cancelled = true; };
  }, [content, size, foreground, background, errorCorrection]);

  const downloadQrCode = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = 'allsmalltools-qr-code.png';
    link.click();
  };

  const bookmarkPage = () => {
    const shortcut = navigator.userAgent.toLowerCase().includes('mac') ? 'Cmd+D' : 'Ctrl+D';
    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to the QR Code Generator.`);
  };

  const dismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('qr-generator-bookmark-dismissed', 'true');
  };

  const faqs = [
    {
      question: 'Is this QR Code Generator free?',
      answer: 'Yes. You can create and download unlimited QR codes without registration, watermarks, or fees.',
    },
    {
      question: 'Is my QR code data uploaded?',
      answer: 'No. QR code generation happens entirely in your browser. The text or URL you enter is not sent to the AllSmallTools server.',
    },
    {
      question: 'What can I put in a QR code?',
      answer: 'You can encode website URLs, plain text, email addresses, phone numbers, Wi-Fi details, or any other text supported by the scanning app.',
    },
    {
      question: 'What does error correction mean?',
      answer: 'Higher error correction can make a QR code readable even if part of it is damaged or obscured, but it also creates a denser pattern.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>QR Code Generator — Create & Download QR Codes | AllSmallTooLS</title>
        <meta name="description" content="Create a custom QR code for any URL or text. Adjust its size and colors, then download a high-quality PNG for free." />
        <meta name="keywords" content="QR code generator, create QR code, URL QR code, free QR maker, download QR code" />
        <link rel="canonical" href="https://allsmalltools.com/qr-code-generator" />
      </Helmet>

      <div className="space-y-6">
        {showBookmarkBanner && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3 text-center sm:text-left">
                <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0"><QrCode className="h-6 w-6 text-indigo-600" /></div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">Bookmark this page to create QR codes anytime</p>
                  <p className="text-sm text-gray-600 hidden sm:block">Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={bookmarkPage} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 whitespace-nowrap">Bookmark Now</button>
                <button onClick={dismissBookmarkBanner} className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-3 rounded-lg shadow-sm" aria-label="Dismiss bookmark banner">✕</button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-4">
          <Link to="/"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Back to Home</Button></Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2"><QrCode className="h-8 w-8 text-cyan-600" /><span>QR Code Generator</span></h1>
            <p className="text-gray-600">Turn any URL or text into a custom, downloadable QR code</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader><CardTitle>QR Code Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="qr-content">Text or URL</Label>
                <textarea id="qr-content" value={content} onChange={(event) => setContent(event.target.value)} maxLength={2000} placeholder="Enter a website URL or any text" className="w-full min-h-32 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y" />
                <p className="text-xs text-gray-500 text-right">{content.length}/2000 characters</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4"><Label htmlFor="qr-size">Image Size</Label><Input id="qr-size" type="number" min="160" max="1024" value={size} onChange={(event) => setSize(Math.min(1024, Math.max(160, Number(event.target.value) || 320)))} className="w-28 text-center" /></div>
                <Slider value={[size]} min={160} max={1024} step={16} onValueChange={([value]) => setSize(value)} aria-label="QR code image size" />
                <div className="flex justify-between text-xs text-gray-500"><span>160 px</span><span>1024 px</span></div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="qr-foreground">QR Color</Label><div className="flex gap-2"><Input id="qr-foreground" type="color" value={foreground} onChange={(event) => setForeground(event.target.value)} className="w-14 p-1" /><Input value={foreground} onChange={(event) => setForeground(event.target.value)} aria-label="QR foreground color value" /></div></div>
                <div className="space-y-2"><Label htmlFor="qr-background">Background Color</Label><div className="flex gap-2"><Input id="qr-background" type="color" value={background} onChange={(event) => setBackground(event.target.value)} className="w-14 p-1" /><Input value={background} onChange={(event) => setBackground(event.target.value)} aria-label="QR background color value" /></div></div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="qr-correction">Error Correction</Label>
                <select id="qr-correction" value={errorCorrection} onChange={(event) => setErrorCorrection(event.target.value)} className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                  <option value="L">Low — smallest pattern</option><option value="M">Medium — recommended</option><option value="Q">Quartile — resilient</option><option value="H">High — most resilient</option>
                </select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>QR Code Result</CardTitle></CardHeader>
            <CardContent>
              {qrDataUrl ? (
                <div className="space-y-6 text-center">
                  <div className="min-h-[360px] rounded-lg border border-cyan-200 bg-cyan-50 p-6 flex items-center justify-center"><img src={qrDataUrl} alt="Generated QR code preview" className="max-w-full max-h-[340px] rounded shadow-sm" /></div>
                  <div className="flex items-center justify-center gap-2 text-sm text-green-700"><ShieldCheck className="h-5 w-5" /><span>Generated privately in your browser</span></div>
                  <Button onClick={downloadQrCode} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"><Download className="h-4 w-4 mr-2" />Download QR Code as PNG</Button>
                </div>
              ) : (
                <div className="text-center py-16 text-gray-500"><QrCode className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No QR code generated</p><p className="text-sm mt-1">Enter text or a URL to begin</p>{error && <p className="text-sm text-red-600 mt-3">{error}</p>}</div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mt-12">
          <CardHeader><h2 className="text-2xl font-bold text-gray-900">Why Use Our QR Code Generator?</h2></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[[Zap, 'Fast & Easy', 'Create a ready-to-scan QR code instantly as you type, with no account or software installation.'], [Lock, 'Private by Design', 'Your content stays in your browser and is never uploaded to our servers for QR generation.'], [Smartphone, 'Custom & Downloadable', 'Choose the size, colors, and error correction, then download a clean PNG for sharing or printing.']].map(([Icon, title, description]) => (
                <div key={title} className="flex flex-col items-start space-y-3"><div className="bg-cyan-100 p-3 rounded-lg"><Icon className="h-6 w-6 text-cyan-700" /></div><h3 className="text-lg font-semibold text-gray-900">{title}</h3><p className="text-gray-600">{description}</p></div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg"><p className="text-gray-700">Create QR codes for websites, menus, contact details, event information, product packaging, signs, presentations, and more. Always test the downloaded code with a phone before printing or distributing it.</p></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="text-xl font-bold text-gray-900">How to Create a QR Code</h2></CardHeader>
          <CardContent><ol className="space-y-4 text-gray-700">{[['Enter Your Content', 'Paste a URL or type the text you want the QR code to contain.'], ['Customize the Design', 'Choose the image size, foreground color, background color, and error-correction level.'], ['Preview Your QR Code', 'The preview updates automatically whenever you change the content or settings.'], ['Download and Test', 'Download the PNG and scan it with a phone before using it publicly.']].map(([title, description], index) => <li key={title} className="flex items-start space-x-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-semibold">{index + 1}</span><div><strong>{title}:</strong> {description}</div></li>)}</ol></CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2></CardHeader>
          <CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{faqs.map((faq, index) => <div key={faq.question} className="border border-gray-200 rounded-lg overflow-hidden"><button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors" aria-expanded={openFaq === index}><h3 className="font-semibold text-gray-900">{faq.question}</h3>{openFaq === index ? <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />}</button>{openFaq === index && <div className="p-4 bg-white"><p className="text-gray-700">{faq.answer}</p></div>}</div>)}</div></CardContent>
        </Card>
      </div>
    </>
  );
};

export default QrCodeGeneratorPage;
